import { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Chip,
  Skeleton,
  Drawer,
  Divider,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { START_GUIDE_GENERATION } from '../graphql/mutations';
import { GET_FEATURED_GUIDES, GET_GUIDE_LANDING, GET_PUBLIC_PLAN } from '../graphql/queries';
import { auth } from '../config/firebase';
import AIInputComposer, { type GenerationMode } from '../components/AIInputComposer';
import { ProcessGraph } from '../components/ProcessGraph';
import GuideCreationDialog from '../components/GuideCreationDialog';
import { PlanPhaseAccordion } from '../components/PlanPhaseAccordion';
import { useGoalAnalysisFlow } from '../hooks/useGoalAnalysisFlow';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useToast } from '../contexts/ToastContext';
import { buildGuideUrl } from '../utils/urls';
import { type FeaturedGuide, type Plan } from '../types/generated/graphql';
import { type ChipDef, EXAMPLE_CHIPS, PERSONA_COLORS } from '../data/exampleChips';

// Trending chips – handpicked from the persona groups
const TRENDING_CHIPS: { label: string; icon: string; persona: string }[] = [
  { label: 'FOIA request processing', icon: '🏛️', persona: 'Government' },
  { label: 'Grant submission portal', icon: '📨', persona: 'Education' },
];

// Card header gradients derived from persona colors
const HEADER_GRADIENTS: Record<string, string> = {
  Government: 'linear-gradient(135deg, #FFF7ED 0%, #FFECD2 50%, #FED7AA 100%)',
  'Client Work': 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 50%, #BFDBFE 100%)',
  'Teams & Ops': 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 50%, #BBF7D0 100%)',
  Education: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 50%, #E9D5FF 100%)',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function HomePage() {
  useDocumentTitle('Planapus');

  const [prompt, setPrompt] = useState('');
  const { user } = useAuth();
  const { activeOrg } = useOrg();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // --- Guide drawer state ---
  const [selectedGuide, setSelectedGuide] = useState<FeaturedGuide | null>(null);

  // Fetch full guide landing data when a guide is selected (for steps, templatePlanId)
  const { data: guideLandingData, loading: guideLandingLoading } = useQuery(GET_GUIDE_LANDING, {
    variables: { orgSlug: selectedGuide?.orgSlug ?? '', guideSlug: selectedGuide?.slug ?? '' },
    skip: !selectedGuide,
  });
  const guideLanding = guideLandingData?.guideLanding;

  // If guide has a backing plan, fetch it for ProcessGraph + PlanPhaseAccordion
  const templatePlanId = guideLanding?.templatePlanId;
  const { data: planData, loading: planLoading } = useQuery(GET_PUBLIC_PLAN, {
    variables: { planId: templatePlanId! },
    skip: !templatePlanId,
  });
  const drawerPlan = planData?.getPublicPlan as Plan | undefined;

  // --- Guide generation (async bg job) ---
  const [startGuideGeneration, { loading: startingGuide }] = useMutation(START_GUIDE_GENERATION);
  const [generationJobId, setGenerationJobId] = useState<string | null>(null);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const { data: featuredData } = useQuery(GET_FEATURED_GUIDES);
  const featuredGuides: FeaturedGuide[] = useMemo(
    () => featuredData?.featuredGuides ?? [],
    [featuredData],
  );

  const getAuthToken = async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Failed to get authentication token');
    return token;
  };

  const callGenerateGuide = async (
    promptText: string,
    planInput?: Record<string, unknown>,
    attachmentIds?: string[],
  ) => {
    const token = await getAuthToken();

    const { data } = await startGuideGeneration({
      variables: {
        prompt: promptText,
        orgSlug: activeOrg?.slug || '',
        ...(planInput && { planInput }),
        ...(attachmentIds && attachmentIds.length > 0 && { attachmentIds }),
      },
      context: { headers: { authorization: `Bearer ${token}` } },
    });

    const result = data?.startGuideGeneration;
    if (result?.deduplicated && result?.resultRef) {
      navigate(`/org/guides/${result.resultRef}/edit`);
    } else if (result?.jobId) {
      setPrompt('');
      setGenerationJobId(result.jobId);
      setGenerationDialogOpen(true);
    }
  };

  const { startAnalysis, isAnalyzing, isGenerating, dialogElement } = useGoalAnalysisFlow({
    onGenerate: async (payload) => {
      const planInput: Record<string, unknown> = {
        prompt: payload.prompt,
        analysisResult: payload.analysisResult,
        clarifyingAnswers: payload.clarifyingAnswers,
      };
      await callGenerateGuide(payload.prompt, planInput, payload.attachmentIds);
    },
    onSkip: async (payload) => {
      await callGenerateGuide(payload.prompt, undefined, payload.attachmentIds);
    },
  });

  const handleComposerSubmit = async (text: string, attachmentIds: string[], mode: GenerationMode) => {
    if (!text.trim() && attachmentIds.length === 0) return;

    if (!user) {
      showToast({ message: 'Please sign in to create a guide', severity: 'info' });
      navigate('/auth');
      return;
    }

    if (!activeOrg) {
      showToast({ message: 'Please create or select an organization first', severity: 'error' });
      navigate('/org/new');
      return;
    }

    try {
      if (mode === 'analyze') {
        await startAnalysis(text, attachmentIds);
      } else {
        await callGenerateGuide(text, undefined, attachmentIds);
      }
    } catch (error: unknown) {
      console.error('Error:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to generate',
        severity: 'error',
      });
    }
  };

  // --- Chip click handler ---
  const handleChipClick = useCallback(
    (chip: ChipDef) => {
      // If chip has a guideRef and we find a matching featured guide, open the drawer
      if (chip.guideRef) {
        const match = featuredGuides.find(
          (g) => g.orgSlug === chip.guideRef!.orgSlug && g.slug === chip.guideRef!.slug,
        );
        if (match) {
          setSelectedGuide(match);
          return;
        }
      }
      // Otherwise populate the prompt with the specific prompt text
      setPrompt(chip.promptText);
    },
    [featuredGuides],
  );

  // --- Trending chip click: find matching ChipDef and delegate ---
  const handleTrendingClick = useCallback(
    (label: string) => {
      for (const group of EXAMPLE_CHIPS) {
        const chip = group.chips.find((c) => c.label === label);
        if (chip) {
          handleChipClick(chip);
          return;
        }
      }
    },
    [handleChipClick],
  );

  const closeDrawer = () => setSelectedGuide(null);

  const isLoading = isAnalyzing || startingGuide || isGenerating;

  // -----------------------------------------------------------------------
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: `
          radial-gradient(ellipse at 15% 5%, rgba(219, 234, 254, 0.45) 0%, transparent 50%),
          radial-gradient(ellipse at 85% 5%, rgba(237, 233, 254, 0.45) 0%, transparent 50%),
          radial-gradient(ellipse at 50% 95%, rgba(186, 230, 253, 0.35) 0%, transparent 40%),
          radial-gradient(ellipse at 10% 80%, rgba(187, 247, 208, 0.25) 0%, transparent 35%),
          radial-gradient(ellipse at 90% 80%, rgba(187, 247, 208, 0.25) 0%, transparent 35%),
          linear-gradient(180deg,
            #faf8ff 0%,
            #f0f5ff 18%,
            #f5faf5 42%,
            #eef7ee 62%,
            #e0f0f8 82%,
            #d5eaf5 100%
          )
        `,
        minHeight: '100vh',
      }}
    >
      {/* ──── Hero ──── */}
      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 }, pb: 1, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 45%, #C2410C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Turn any process into a guide
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 3, lineHeight: 1.5 }}>
          Steps, roles, docs, and tools — organized and ready to run.
        </Typography>
      </Container>

      {/* ──── AI Input Composer (search bar) ──── */}
      <Container maxWidth="sm" sx={{ pb: 2 }}>
        <AIInputComposer
          value={prompt}
          onChange={setPrompt}
          onSubmit={handleComposerSubmit}
          loading={isLoading}
          placeholder="Describe your process or paste an SOP..."
        />
      </Container>

      {/* ──── Trending ──── */}
      <Container maxWidth="sm" sx={{ pb: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TrendingUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Trending
            </Typography>
          </Stack>
          {TRENDING_CHIPS.map((tc) => {
            const palette = PERSONA_COLORS[tc.persona];
            return (
              <Chip
                key={tc.label}
                icon={<Typography sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{tc.icon}</Typography>}
                label={tc.label}
                variant="outlined"
                onClick={() => handleTrendingClick(tc.label)}
                sx={{
                  borderColor: palette?.border ?? 'divider',
                  color: palette?.text ?? 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  '&:hover': { borderColor: palette?.accent, bgcolor: palette?.bg },
                }}
              />
            );
          })}
        </Stack>
      </Container>

      {/* ──── Category Cards ──── */}
      <Container maxWidth="lg" sx={{ pb: 3 }}>
        <Grid container spacing={2}>
          {EXAMPLE_CHIPS.map((group) => {
            const palette = PERSONA_COLORS[group.persona];
            return (
              <Grid key={group.persona} size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: palette.border,
                    bgcolor: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(8px)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Colored header with illustration */}
                  <Box
                    sx={{
                      height: 110,
                      background: HEADER_GRADIENTS[group.persona],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '3.5rem', lineHeight: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }}>
                      {group.icon}
                    </Typography>
                  </Box>

                  {/* Title */}
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{group.icon}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: palette.text, fontSize: '1.05rem' }}>
                        {group.persona}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Chips */}
                  <Box sx={{ px: 2, pb: 2, pt: 1, flex: 1 }}>
                    <Stack spacing={0.75}>
                      {group.chips.slice(0, 3).map((chip) => (
                        <Paper
                          key={chip.label}
                          elevation={0}
                          onClick={() => handleChipClick(chip)}
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '20px',
                            cursor: 'pointer',
                            bgcolor: palette.bg,
                            border: '1px solid',
                            borderColor: chip.guideRef ? palette.accent : palette.border,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${palette.border}80`,
                              borderColor: palette.accent,
                            },
                            '&:active': { transform: 'translateY(0)' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: palette.text, fontSize: '0.8rem' }}
                          >
                            {chip.label}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* ──── Footer ──── */}
      <Box sx={{ py: 2, textAlign: 'center', mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary">
          AI-generated. Review before using.
        </Typography>
      </Box>

      {/* ──── Right-side Drawer: Guide Preview ──── */}
      <Drawer
        anchor="right"
        open={!!selectedGuide}
        onClose={closeDrawer}
        variant="temporary"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480, md: 520 },
            borderLeft: 1,
            borderColor: 'divider',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.06)',
          },
        }}
      >
        {selectedGuide && (
          <>
            {/* Drawer header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}
              >
                Guide Preview
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => navigate(buildGuideUrl(selectedGuide.orgSlug, selectedGuide.slug))}
                  title="Open full guide page"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={closeDrawer}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Drawer content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {guideLandingLoading ? (
                <Box sx={{ p: 3 }}>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="90%" sx={{ mt: 1 }} />
                  <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
                </Box>
              ) : guideLanding ? (
                <Box>
                  {/* Guide header */}
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                      {guideLanding.title}
                    </Typography>
                    {selectedGuide.publisherOrgName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        by {selectedGuide.publisherOrgName}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5 }}>
                      {guideLanding.description}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`${guideLanding.steps?.length ?? 0} steps`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {selectedGuide.isOfficial && (
                        <Chip
                          label="Official"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Plan preview (if available) */}
                  {templatePlanId && (
                    <>
                      {planLoading ? (
                        <Box sx={{ p: 2 }}>
                          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" height={100} sx={{ mt: 2, borderRadius: 1 }} />
                        </Box>
                      ) : drawerPlan ? (
                        <>
                          {/* Process graph */}
                          <Box sx={{ px: 1, pt: 1 }}>
                            <ProcessGraph plan={drawerPlan} height={280} />
                          </Box>

                          <Divider />

                          {/* Phase accordion */}
                          <Box sx={{ px: 1, pb: 2 }}>
                            <PlanPhaseAccordion plan={drawerPlan} showRoles />
                          </Box>
                        </>
                      ) : null}
                    </>
                  )}

                  {/* Fallback: guide steps list (when no backing plan data is available) */}
                  {!planLoading && !drawerPlan && guideLanding.steps && guideLanding.steps.length > 0 && (
                    <Box sx={{ px: 1.5, pb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          fontSize: '0.65rem',
                          color: 'text.secondary',
                          px: 1,
                          pt: 1.5,
                          pb: 0.5,
                        }}
                      >
                        Steps
                      </Typography>
                      <List dense disablePadding>
                        {[...guideLanding.steps]
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((step: any) => (
                            <ListItem key={step.id} sx={{ px: 1 }}>
                              <ListItemText
                                primary={step.title}
                                secondary={step.summary}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}

                  <Divider />

                  {/* CTAs */}
                  <Box sx={{ p: 2.5, display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => navigate(buildGuideUrl(selectedGuide.orgSlug, selectedGuide.slug))}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                      View full guide
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        if (user) {
                          navigate(buildGuideUrl(selectedGuide.orgSlug, selectedGuide.slug));
                        } else {
                          showToast({ message: 'Sign in to start this guide', severity: 'info' });
                          navigate('/auth');
                        }
                      }}
                      sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
                    >
                      Start this guide
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Guide not found
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Drawer>

      {/* Clarification Dialog (analyze mode) */}
      {dialogElement}

      {/* Guide Creation Progress Dialog */}
      <GuideCreationDialog
        open={generationDialogOpen}
        jobId={generationJobId}
        onClose={() => { setGenerationDialogOpen(false); setGenerationJobId(null); }}
      />
    </Box>
  );
}

export default HomePage;
